import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { policiesApi } from '@/api/clients/policiesApi'
import type { Waiver } from '@/api/types'
import { PageHeader, Card, Button, Field, TextInput, SelectInput, TextArea } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { QueryState } from '@/components/common/State'
import { RoleGate } from '@/components/common/RoleGate'
import { formatDate } from '@/components/common/format'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  pending: 'warning',
  expired: 'neutral',
  revoked: 'error',
}

export default function Waivers() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [approveTarget, setApproveTarget] = useState<Waiver | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<Waiver | null>(null)

  const waivers = useQuery({
    queryKey: ['waivers', orgId],
    queryFn: () => policiesApi.waivers(orgId),
    enabled: !!orgId,
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['waivers', orgId] })

  return (
    <div>
      <PageHeader
        title="Waivers"
        subtitle="Risk-accepted exceptions with expiry, approver chain, and rationale"
        actions={
          <RoleGate required="policy_author">
            <Button variant="primary" icon="add" onClick={() => setShowCreate(true)}>
              New waiver
            </Button>
          </RoleGate>
        }
      />

      <Card className="!p-0">
        <QueryState
          isLoading={waivers.isLoading}
          error={waivers.error}
          isEmpty={waivers.data?.length === 0}
          emptyProps={{ icon: 'verified_user', title: 'No waivers' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Scope</th>
                <th className="px-5 py-3 font-medium">Rationale</th>
                <th className="px-5 py-3 font-medium">Approvals</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Expiry</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {waivers.data?.map((w) => {
                const roles = Object.entries(w.approvers ?? {})
                const signed = roles.filter(([, a]) => a?.approved_at).length
                return (
                  <tr key={w.id}>
                    <td className="px-5 py-3">
                      <Badge label={w.scope_type} variant="info" />
                    </td>
                    <td className="px-5 py-3 text-text-light dark:text-text-dark max-w-xs truncate">
                      {w.rationale}
                    </td>
                    <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                      {roles.length ? `${signed}/${roles.length}` : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <Badge label={w.status} variant={STATUS_VARIANT[w.status] ?? 'neutral'} />
                    </td>
                    <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                      {formatDate(w.expiry)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {w.status === 'pending' && (
                        <RoleGate required={['security_owner', 'waiver_approver']}>
                          <Button variant="ghost" icon="check" onClick={() => setApproveTarget(w)}>
                            Approve
                          </Button>
                        </RoleGate>
                      )}
                      {w.status === 'active' && (
                        <RoleGate required="security_owner">
                          <Button variant="ghost" icon="block" onClick={() => setRevokeTarget(w)}>
                            Revoke
                          </Button>
                        </RoleGate>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </QueryState>
      </Card>

      {showCreate && (
        <CreateWaiverModal
          orgId={orgId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            invalidate()
            setShowCreate(false)
          }}
        />
      )}
      {approveTarget && (
        <ApproveWaiverModal
          orgId={orgId}
          waiver={approveTarget}
          onClose={() => setApproveTarget(null)}
          onDone={() => {
            invalidate()
            setApproveTarget(null)
          }}
        />
      )}
      {revokeTarget && (
        <RevokeWaiverModal
          orgId={orgId}
          waiver={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onDone={() => {
            invalidate()
            setRevokeTarget(null)
          }}
        />
      )}
    </div>
  )
}

function CreateWaiverModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string
  onClose: () => void
  onCreated: () => void
}) {
  const policies = useQuery({
    queryKey: ['policies', orgId],
    queryFn: () => policiesApi.list(orgId),
    enabled: !!orgId,
  })
  const [policyId, setPolicyId] = useState('')
  const [ruleIds, setRuleIds] = useState<string[]>([])
  const [scopeType, setScopeType] = useState('asset')
  const [scopeId, setScopeId] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [expiry, setExpiry] = useState('')
  const [rationale, setRationale] = useState('')

  const rules = useMemo(
    () => policies.data?.find((p) => p.id === policyId)?.rules ?? [],
    [policies.data, policyId]
  )

  const mut = useMutation({
    mutationFn: () =>
      policiesApi.createWaiver(orgId, {
        policy_id: policyId,
        rule_ids: ruleIds,
        scope_type: scopeType,
        scope_id: scopeType === 'org' ? null : scopeId || null,
        effective_from: effectiveFrom,
        expiry,
        rationale,
      }),
    onSuccess: onCreated,
  })

  const toggleRule = (rid: string) =>
    setRuleIds((prev) => (prev.includes(rid) ? prev.filter((r) => r !== rid) : [...prev, rid]))

  const valid =
    policyId &&
    ruleIds.length > 0 &&
    effectiveFrom &&
    expiry &&
    rationale.length >= 20 &&
    (scopeType === 'org' || scopeId)

  return (
    <Modal
      title="New waiver"
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Policy">
          <SelectInput value={policyId} onChange={(e) => { setPolicyId(e.target.value); setRuleIds([]) }}>
            <option value="">Select a policy…</option>
            {policies.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </SelectInput>
        </Field>

        {policyId && (
          <div>
            <p className="text-sm font-medium text-text-light dark:text-text-dark mb-1">Rules to waive</p>
            {rules.length === 0 ? (
              <p className="text-xs text-subtle-light dark:text-subtle-dark">This policy has no rules.</p>
            ) : (
              <div className="space-y-1">
                {rules.map((r) => (
                  <label key={r.rule_id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={ruleIds.includes(r.rule_id)}
                      onChange={() => toggleRule(r.rule_id)}
                    />
                    <span className="font-mono text-xs">{r.rule_id}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Scope type">
            <SelectInput value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
              {['asset', 'inventory', 'source', 'org'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectInput>
          </Field>
          {scopeType !== 'org' && (
            <Field label={`Scope ID (${scopeType})`}>
              <TextInput value={scopeId} onChange={(e) => setScopeId(e.target.value)} placeholder="UUID" />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Effective from">
            <TextInput type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Expiry (≤ 12 months)">
            <TextInput type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </Field>
        </div>

        <Field label="Rationale (≥ 20 chars)">
          <TextArea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} />
        </Field>
        {mut.error && <p className="text-sm text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function ApproveWaiverModal({
  orgId,
  waiver,
  onClose,
  onDone,
}: {
  orgId: string
  waiver: Waiver
  onClose: () => void
  onDone: () => void
}) {
  const ladder = Object.keys(waiver.approvers ?? {})
  const [role, setRole] = useState(
    ladder.find((r) => !waiver.approvers?.[r]?.approved_at) ?? ladder[0] ?? ''
  )
  const [rationale, setRationale] = useState('')

  const mut = useMutation({
    mutationFn: () => policiesApi.approveWaiver(orgId, waiver.id, role, rationale),
    onSuccess: onDone,
  })

  return (
    <Modal
      title="Approve waiver"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!role || rationale.length < 20 || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? 'Approving…' : 'Approve'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Sign as role">
          {ladder.length ? (
            <SelectInput value={role} onChange={(e) => setRole(e.target.value)}>
              {ladder.map((r) => (
                <option key={r} value={r}>
                  {r} {waiver.approvers?.[r]?.approved_at ? '(signed)' : ''}
                </option>
              ))}
            </SelectInput>
          ) : (
            <TextInput value={role} onChange={(e) => setRole(e.target.value)} placeholder="role" />
          )}
        </Field>
        <Field label="Rationale (≥ 20 chars)">
          <TextArea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} />
        </Field>
        {mut.error && <p className="text-sm text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function RevokeWaiverModal({
  orgId,
  waiver,
  onClose,
  onDone,
}: {
  orgId: string
  waiver: Waiver
  onClose: () => void
  onDone: () => void
}) {
  const [rationale, setRationale] = useState('')
  const mut = useMutation({
    mutationFn: () => policiesApi.revokeWaiver(orgId, waiver.id, rationale),
    onSuccess: onDone,
  })
  return (
    <Modal
      title="Revoke waiver"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={rationale.length < 20 || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? 'Revoking…' : 'Revoke'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-subtle-light dark:text-subtle-dark">
          Revoking re-enforces the waived rules immediately.
        </p>
        <Field label="Rationale (≥ 20 chars)">
          <TextArea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} />
        </Field>
        {mut.error && <p className="text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}
